import { mapState, mapGetters } from 'vuex';
import ApiService from '@/services/apiService';
import { randomPassword, isNullOrEmpty } from '@/utils/vuex';
import pullAt from 'lodash/pullAt';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import get from 'lodash/get';
export default {
  name: 'CoreTenantDialogCreateUser',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    tenantId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      tenantDetails: null,
      userKeyValue: [],
      loading: false,
      valid: false,
      originalUser: null,
      createdUser: {
        firstName: null,
        lastName: null,
        email: null,
        password: '',
        active: true,
        properties: {
          values: [{
            key: '',
            value: ''
          }]
        },
        scope: null,
        access: []
      },
      defaultPasswordPolicy: {},
      editFormValidity: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color', 'permissions']),
    ...mapState('authentication', ['user']),
    ...mapGetters({
      sessionUserId: 'authentication/userId',
      sessionPrimaryTenantIds: 'authentication/primaryTenantIds',
      sessionAccess: 'authentication/access'
    }),
    mode() {
      return this.$route.params.id ? 'update' : 'create';
    },
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    passwordRules() {
      return [v => !!v || this.$i18n_t('accMgmt.user.passwordErrors.USER_PASSWORD_REQUIRED'), v => v.length >= this.defaultPasswordPolicy.length || this.$i18n_tc('accMgmt.user.passwordErrors.USER_PASSWORD_MINLENGTH', this.defaultPasswordPolicy.length, {
        n: this.defaultPasswordPolicy.length
      })];
    },
    emailRules() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired'), v => /.+@.+\..+/.test(v) || this.$i18n_t('Common.emailValid')];
    }
  },
  mounted() {
    this.$store.state.app.loading = false;
    return this.fetchTenantData().then(() => {
      if (this.tenantId) {
        return ApiService.getUser(this, this.$store.state.authentication.user.id).then(data => {
          this.setUserDetails(data);
        }).finally(() => {
          this.$store.state.app.loading = false;
        });
      } else {
        this.setUserDetails({
          active: true
        });
      }
    });
  },
  methods: {
    dataCyFromPropertyKey(key, name) {
      const propName = key.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return propName ? `${name}_${propName}` : undefined;
    },
    requiredRuleKeyValue(kv) {
      return [v => isNullOrEmpty(kv.key) && isNullOrEmpty(kv.value) || !!v || this.$i18n_t('Common.fieldRequired')];
    },
    didSelectScope(scope) {
      this.createdUser.scope = scope;
      this.fetchDefaultRoles(scope.id);
    },
    fetchDefaultRoles(scopeId) {
      ApiService.fetchDefaultRoles(scopeId, true).then(roles => {
        this.createdUser.access = [{
          tenantId: get(roles, '[0].tenantId') || this.tenantId,
          scopeId: scopeId,
          Scope: {
            id: scopeId
          },
          roles,
          primary: true
        }];
        this.createdUser = cloneDeep(this.createdUser);
      }).catch(() => {
        this.$snack(this.$i18n_t('accMgmt.user.createFailedSnack'), 'error');
      });
    },
    fetchTenantData() {
      return Promise.all([ApiService.tenantGetPasswordPolicy(),
      // NOTE: using `no-descendents` to not get any child-data; this will ALSO _NOT_ load any scope-descendents
      ApiService.tenantGetById(this.tenantId, ['no-descendents'])]).then(data => {
        this.defaultPasswordPolicy = data[0];
        const tenant = data[1];
        this.tenantDetails = tenant;
        return tenant;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack('Failed to fetch tenant', 'error');
        }
        this.$router.push({
          path: `/account-mgmt/${this.tenantId}?userlist`
        });
      });
    },
    validate() {
      this.valid = this.$refs && this.$refs.form ? this.$refs.form.validate() : false;
      if (this.valid) {
        this.snackbar = true;
      }
    },
    handleSubmit() {
      this.validate();
      if (this.valid) {
        this.saveUser(this.createdUser, this.mode);
      }
    },
    saveUser(userInfo) {
      this.loading = true;
      userInfo.properties.values = get(this, 'userKeyValue', []).filter(kv => {
        return kv.key !== '' && kv.value !== '';
      });
      this.$emit('create-user', userInfo);
    },
    addKeyValuePair() {
      this.userKeyValue = cloneDeep(this.userKeyValue.concat([{
        key: '',
        value: ''
      }]));
    },
    removeKeyValuePair(index) {
      pullAt(this.userKeyValue, [index]);
      if (this.userKeyValue.length === 0) {
        this.addKeyValuePair();
      }
      // trigger a UI refresh
      this.userKeyValue = cloneDeep(this.userKeyValue);
    },
    setUserDetails() {
      this.createdUser.password = randomPassword();
      if (!get(this.createdUser, 'access')) {
        set(this.createdUser, 'access', []);
      }
      const userDefaultProperties = get(this.tenantDetails, 'data.userDefaultProperties', []);
      const defaultProperties = userDefaultProperties.map(key => ({
        key,
        value: ''
      }));
      this.createdUser.properties.values = defaultProperties;
      this.userKeyValue = defaultProperties.length > 0 ? defaultProperties : [{
        key: '',
        value: ''
      }];
      this.validate();
    },
    updatedUserAccess(access) {
      this.createdUser.access = access;
      if ((access || []).length === 0) {
        delete this.createdUser.scope;
      }
    },
    close() {
      this.$emit('closeUserDialog');
    }
  }
};